import { useEffect, useRef } from 'react';
import { User } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { setUserId, setUserProperties, event } from '../utils/analytics';
import { useCredits } from '../contexts/CreditContext';
import posthog from 'posthog-js';
import { useStudioPageLoadNavigation } from './DatasetMarketplace/useStudioPageLoadTiming';

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

export const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  useStudioPageLoadNavigation();
  const { user } = useAuth();
  const { profile } = useCredits();
  const prevUserRef = useRef<User | null | undefined>(undefined);

  useEffect(() => {
    if (user) {
      setUserId(user.uid);
      
      const userProperties: Record<string, any> = {
        email: user.email,
        email_verified: user.emailVerified,
        provider: user.providerData[0]?.providerId || 'unknown',
        created_at: user.metadata.creationTime,
      };

      // Add profile-based properties if available
      if (profile) {
        userProperties.organization = profile.org_name || 'none';
        userProperties.organization_id = profile.org_id || 'none';
        // credits is only populated once /credits/balance has been fetched
        if (typeof profile.credits === 'number') {
          userProperties.has_credits = profile.credits > 0;
        }
        userProperties.account_type = profile.org_id ? 'organization' : 'individual';
      }
      
      setUserProperties(userProperties);

      // Identify user in PostHog so sessions are linked to real users
      const isInternal = user.email?.endsWith('@nomadicml.com') || false;
      posthog.identify(user.uid, {
        email: user.email,
        name: user.displayName || '',
        email_verified: user.emailVerified,
        provider: user.providerData[0]?.providerId || 'unknown',
        created_at: user.metadata.creationTime,
        organization: profile?.org_name || 'none',
        organization_id: profile?.org_id || 'none',
        account_type: profile?.org_id ? 'organization' : 'individual',
        is_internal: isInternal,
      });

      event('user_login', {
        method: user.providerData[0]?.providerId || 'unknown',
        organization: profile?.org_name || 'none',
      });
    } else {
      if (prevUserRef.current) posthog.reset();
      event('user_logout', {});
    }
    prevUserRef.current = user;
  }, [user, profile]);

  return <>{children}</>;
};
